/**
 * Тариф, подписка, платежи.
 *
 * Главное отличие от привычного: **портала управления подпиской у ЮKassa нет**,
 * в отличие от Stripe. Управление живёт у нас, поэтому `GET /billing/portal`
 * отдаёт весь экран тарифа одним запросом, а изменения делаются нашими же
 * эндпоинтами, а не уходом к провайдеру.
 *
 * Второе отличие важнее и определяет весь экран оплаты: **возврат клиента из
 * платёжки ничего не подтверждает**. Деньги подтверждает вебхук или сверка;
 * вернуться на адрес возврата можно и руками. Поэтому статус платежа всегда
 * перечитывается запросом, а частоту опроса задаёт сервер заголовком
 * `Retry-After` (docs/API.md §10).
 */

import {
  api,
  downloadFile,
  idempotencyKey,
  requestWith,
  type Page,
  type Result,
} from './client';
import type { components } from './schema';

export type BillingPortal = components['schemas']['BillingPortal'];
export type Plan = components['schemas']['Plan'];
export type PlanAddon = components['schemas']['PlanAddon'];
export type PlanCatalog = components['schemas']['PlanCatalog'];
export type Subscription = components['schemas']['Subscription'];
export type SubscriptionAddon = components['schemas']['SubscriptionAddon'];
export type Checkout = components['schemas']['Checkout'];
export type CheckoutRequest = components['schemas']['CheckoutRequest'];
export type PlanChangeResult = components['schemas']['PlanChangeResult'];
export type PlanChangePreview = components['schemas']['PlanChangePreview'];
export type Payment = components['schemas']['Payment'];
export type PaymentDetail = components['schemas']['PaymentDetail'];
export type PaymentStatus = components['schemas']['PaymentStatus'];
export type QuotaUsage = components['schemas']['QuotaUsage'];
export type AutopayState = components['schemas']['AutopayState'];
export type BillingDocument = components['schemas']['BillingDocument'];
export type BillingPeriodUnit = components['schemas']['BillingPeriodUnit'];
export type InvoiceRequest = components['schemas']['InvoiceRequest'];

/** Экран тарифа одним запросом. */
export function getBillingPortal(signal?: AbortSignal): Promise<BillingPortal> {
  return api.get<BillingPortal>('/billing/portal', { signal });
}

/** Публичный справочник тарифов. Нужен там, где подписки ещё нет вовсе. */
export function getPlanCatalog(signal?: AbortSignal): Promise<PlanCatalog> {
  return api.get<PlanCatalog>('/public/plans', { signal, quiet: true });
}

/**
 * Создание платежа. Ключ идемпотентности **передаётся снаружи**: при повторе
 * после подтверждения действия он обязан быть тем же, иначе повтор создаст
 * второй платёж (docs/API.md §6).
 */
export function createCheckout(
  body: CheckoutRequest,
  key: string,
  stepUpToken?: string,
): Promise<Checkout> {
  return api.post<Checkout>('/billing/checkout', body, {
    idempotencyKey: key,
    stepUpToken,
  });
}

/**
 * Платёж вместе со служебным `Retry-After`. Терминальный ответ приходит без
 * него — это и есть команда перестать опрашивать.
 */
export function getPayment(
  paymentId: string,
  signal?: AbortSignal,
): Promise<Result<PaymentDetail>> {
  return requestWith<PaymentDetail>(
    `/billing/payments/${encodeURIComponent(paymentId)}`,
    { signal },
  );
}

/** Последние платежи. Нужны, когда после возврата не известен идентификатор. */
export function listPayments(signal?: AbortSignal): Promise<Page<Payment>> {
  return api.get<Page<Payment>>('/billing/payments', { signal, query: { limit: 5 } });
}

/**
 * Во что обойдётся смена тарифа. Ничего не создаёт и никуда не уводит.
 *
 * Считает сервер, а не экран: доплата зависит от границ расчётного периода и от
 * фактически уплаченного за него, то есть от состояния, которого у кабинета
 * нет. Повторить формулу на клиенте значит однажды показать не ту сумму,
 * которую спишут, — а хуже ошибки в деньгах на этом экране нет ничего.
 */
export function previewPlanChange(
  planCode: string,
  period: BillingPeriodUnit,
  signal?: AbortSignal,
): Promise<PlanChangePreview> {
  return api.post<PlanChangePreview>(
    '/billing/subscription/plan/preview',
    { plan_code: planCode, period },
    { signal },
  );
}

/**
 * Смена тарифа. Повышение вступает в силу сразу и приходит с платежом,
 * понижение планируется на следующий период — ответ различает это полем
 * `applied`, а не догадкой на экране.
 */
export function changePlan(
  planCode: string,
  period: BillingPeriodUnit,
  /**
   * Ключ идемпотентности **снаружи**, как у `createCheckout` и `addAddon`.
   *
   * Здесь он создавался внутри — `idempotencyKey()` прямо в объекте запроса, —
   * и это ломало ровно тот сценарий, ради которого ключ и нужен. Повышение
   * тарифа стоит денег, поэтому сервер отвечает `403 step_up_required`;
   * `useConfirmed` спрашивает пароль и **повторяет тот же вызов** с токеном —
   * а повтор уходил с новым ключом, то есть для сервера это был другой запрос.
   * Один щелчок по «Перейти сейчас» мог дать две попытки списания, и
   * откатывать их пришлось бы руками через ЮKassa.
   *
   * Вызывающий создаёт ключ один раз до `confirmed(...)` и отдаёт его в
   * замыкание — тогда первый вызов и повтор с подтверждением несут один ключ.
   */
  key: string,
  stepUpToken?: string,
): Promise<PlanChangeResult> {
  return api.post<PlanChangeResult>(
    '/billing/subscription/plan',
    { plan_code: planCode, period, return_url: returnUrl() },
    { idempotencyKey: key, stepUpToken },
  );
}

/**
 * Счёт юрлицу вместо карты.
 *
 * Половина клиентов на российском рынке платит по счёту, и продажа юрлицу без
 * этого пути не закрывается вовсе. Роут в контракте был с самого начала, экран
 * тарифа обещал «Оплата по счёту — обычный путь для юрлица», а вызывать его
 * было некому: кнопки не существовало ни на одном экране.
 *
 * Работает **до** первой подписки: сервер отвечает `409`, если подписка уже
 * есть. Это не ограничение реализации, а смысл операции — счёт открывает
 * подписку и активируется по факту поступления денег.
 *
 * Ключ идемпотентности снаружи, как у `createCheckout`: счёт — документ с
 * номером, и второй такой же на один заказ не нужен никому.
 */
export function createInvoice(
  body: InvoiceRequest,
  key: string,
): Promise<BillingDocument> {
  return api.post<BillingDocument>('/billing/documents', body, {
    idempotencyKey: key,
  });
}

/**
 * Скачать документ файлом.
 *
 * Здесь стояла `<a href target="_blank">`, и приёмка получила ровно то, что
 * такая ссылка даёт при отказе: новая вкладка с голым JSON
 * `{"detail":"Файл документа ещё не сформирован."}`. Сервер теперь отдаёт
 * печатную форму телом ответа, но правило то же, что и в центре отчётов:
 * сначала спрашиваем, и только на согласие уводим браузер за файлом — иначе
 * любой отказ показывается человеку либо сырым телом, либо никак.
 */
export function downloadDocument(documentId: string): Promise<void> {
  return downloadFile(`/billing/documents/${encodeURIComponent(documentId)}/download`);
}

/**
 * Отменить незавершённый платёж.
 *
 * Выход из тупика: клиент ушёл с ЮKassa кнопкой «назад», платёж остался
 * «ждём подтверждения» и запирал и повторное оформление, и повышение тарифа.
 * Сервер перед отменой сверяется с провайдером, поэтому оплаченный платёж
 * отменить нельзя — на него приходит `409`, и подписка активируется.
 */
export function cancelPayment(paymentId: string): Promise<Payment> {
  return api.post<Payment>(`/billing/payments/${encodeURIComponent(paymentId)}/cancel`);
}

export function cancelPendingPlanChange(): Promise<void> {
  return api.delete<void>('/billing/subscription/pending-plan');
}

export function cancelSubscription(
  reason: string,
  stepUpToken?: string,
): Promise<Subscription> {
  return api.post<Subscription>(
    '/billing/subscription/cancel',
    reason ? { reason } : {},
    { stepUpToken },
  );
}

export function resumeSubscription(): Promise<Subscription> {
  return api.post<Subscription>('/billing/subscription/resume');
}

export function addAddon(
  addonCode: string,
  key: string,
  stepUpToken?: string,
): Promise<SubscriptionAddon> {
  return api.post<SubscriptionAddon>(
    '/billing/addons',
    { addon_code: addonCode, quantity: 1 },
    { idempotencyKey: key, stepUpToken },
  );
}

export function removeAddon(addonId: string, stepUpToken?: string): Promise<void> {
  return api.delete<void>(`/billing/addons/${encodeURIComponent(addonId)}`, {
    stepUpToken,
  });
}

export function revokeAutopayConsent(): Promise<void> {
  return api.delete<void>('/billing/autopay/consent');
}

/**
 * Отвязать сохранённый способ оплаты.
 *
 * Это НЕ то же самое, что отзыв согласия: `DELETE /billing/autopay/consent`
 * гасит согласие по 376-ФЗ, а карта остаётся сохранённой у провайдера. Здесь
 * клиент убирает саму карту — сам, из кабинета, без обращения в поддержку.
 * Ровно этого требует ЮKassa от магазина с рекуррентными платежами, и ровно
 * поэтому в кабинете две кнопки, а не одна.
 *
 * Ключа идемпотентности у операции нет и не должно быть: контракт объявляет
 * только `StepUpToken`, а повтор DELETE по тому же адресу второй раз просто
 * ничего не находит.
 */
export function deletePaymentMethod(stepUpToken?: string): Promise<void> {
  return api.delete<void>('/billing/payment-method', { stepUpToken });
}

/**
 * Куда ЮKassa вернёт браузер. Адрес собирается из текущего происхождения, а не
 * из настройки: бандл кабинета отдаётся и с домена портала агентства, и зашитое
 * имя увело бы клиента на чужой сайт.
 *
 * Идентификатора платежа здесь нет и быть не может: адрес нужен раньше, чем
 * платёж создан. Его **дописывает сервер** — `?payment_id=…` появляется в том
 * адресе, который уезжает провайдеру. Экран возврата читает параметр первым.
 */
export function returnUrl(): string {
  return `${window.location.origin}/billing/return`;
}

/**
 * Память вкладки о начатом платеже — запасной путь для экрана возврата.
 *
 * Нужна там, где параметра в адресе нет: клиент вернулся по закладке, по
 * истории браузера или провайдер потерял хвост адреса. Ключ один на весь
 * кабинет и живёт здесь, а не в файле одного экрана: начинают платёж два
 * экрана — оформление первой подписки и повышение тарифа, — а читает третий.
 *
 * Пока ключ лежал в `Checkout.tsx`, повышение тарифа его не трогало вовсе:
 * `sessionStorage` хранил идентификатор брошенной оплаты (он стирается только
 * на терминальном статусе), и вернувшийся из ЮKassa человек читал состояние
 * ЧУЖОГО платежа под заголовком «Платёж».
 */
const RETURN_KEY = 'seeno.checkout.payment';

export function rememberPayment(paymentId: string): void {
  try {
    sessionStorage.setItem(RETURN_KEY, paymentId);
  } catch {
    // Приватный режим — не повод не платить: экран возврата найдёт платёж по
    // параметру адреса, а в крайнем случае по последнему в списке.
  }
}

export function rememberedPayment(): string | null {
  try {
    return sessionStorage.getItem(RETURN_KEY);
  } catch {
    return null;
  }
}

export function forgetPayment(): void {
  try {
    sessionStorage.removeItem(RETURN_KEY);
  } catch {
    // см. выше
  }
}

/** Ключ идемпотентности одной попытки оплаты. Повтор после подтверждения — тот же. */
export { idempotencyKey };
